import { pageTitle } from '@palaccount/config';
import {
  OPP_LOSS_REASONS,
  type OppLossReason,
  type OppStageResponse,
  type OppStatsResponse,
  type OpportunityResponse,
} from '@palaccount/contracts';
import { ArrowDown, ArrowRight, ArrowUp, ChartColumn, Search, TriangleAlert } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useSearchParams } from 'react-router';

import { Ltr, Pct } from '@/components/amount';
import { Gated, GatedGroup } from '@/components/permission-gate';
import { Pager } from '@/components/pager';
import { barPercent, chartValue } from '@/lib/money';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { NativeSelect } from '@/components/ui/native-select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Modal } from '@/features/inventory/modal';
import { useItemPicker } from '@/features/inventory/item-picker';
import { usePartnerPicker } from '@/features/partners/partner-picker';
import { PartnerTypeahead } from '@/features/partners/partner-typeahead';
import { usePagedState, usePageRange } from '@/lib/paging';
import { useCan } from '@/lib/permissions';
import { localizeServerCause } from '@/lib/server-messages';
import { cn } from '@/lib/utils';

import {
  useAdvanceOpportunity,
  useCloseLost,
  useCloseWon,
  useCreateOpportunity,
  useLogOppActivity,
  useOpportunities,
  useOpportunity,
  useOpportunityStats,
  useReorderStages,
  useSetOpportunityStage,
  useSetProbability,
  useSetStageActive,
  useStages,
  useUpdateOpportunity,
  useUpdateStage,
} from './api';
import { CrmTabs } from './crm-tabs';
import { wholeDollars } from './pipeline-money';
import { TaskPanel } from './task-panel';

/**
 * CRM-02 — Opportunities / Pipeline. ONE screen (owner ruling R1) around ONE
 * configured stage list (BR-CRM-02-21): the weighted/raw/open headline, the
 * stalled banner and view, the board, the table with Advance in its action
 * column (BR-CRM-02-27), the detail card with the probability override, the
 * pipeline-by-stage and forecast panels, the permission-gated Configure
 * stages panel (crm.approve — BR-CRM-02-26), and the embedded task panel.
 */

const LOSS_KEYS: Record<OppLossReason, string> = {
  price: 'crm.lsPrice',
  competitor: 'crm.lsCompetitor',
  no_budget: 'crm.lsNoBudget',
  timing: 'crm.lsTiming',
  other: 'crm.lsOther',
};

const STAGE_DOT: Record<string, string> = {
  blue: 'bg-blue-500',
  amber: 'bg-amber-500',
  violet: 'bg-violet-500',
  teal: 'bg-teal-500',
};

/** The table and each board column ask for one block at a time (NFR-42). */
const TABLE_PAGE_SIZE = 25;
const BOARD_PAGE_SIZE = 25;

/**
 * ONE STAGE COLUMN of the board: the same paged route as the table, carrying
 * only its own stage and `status=open`.
 *
 * It is a component rather than a `.map()` body because each column is its own
 * query and its own page, and a hook cannot be called inside a loop — the same
 * shape CRM-01's `BoardColumn` took.
 *
 * The header's THREE figures come from `/stats`, not from the rows below them.
 * That is the point of the split: a column showing the first 25 of 300 deals
 * must still print the value of all 300, or a sales manager reads the board as
 * a pipeline that shrank.
 */
function StageColumn({
  stage,
  totals,
  isLastStage,
  selectedId,
  onSelect,
  onAdvance,
  onWon,
  onLost,
}: {
  stage: OppStageResponse;
  totals: OppStatsResponse['byStage'][number] | undefined;
  isLastStage: boolean;
  selectedId: string | undefined;
  onSelect: (id: string) => void;
  onAdvance: (id: string) => void;
  onWon: (opp: OpportunityResponse) => void;
  onLost: (opp: OpportunityResponse) => void;
}) {
  const paging = usePagedState(BOARD_PAGE_SIZE, `stage-${stage.position}`);
  const column = useOpportunities(
    { status: 'open', stageId: stage.id },
    paging.page,
    BOARD_PAGE_SIZE,
  );
  const range = usePageRange(column.data, BOARD_PAGE_SIZE);

  return (
    <div className="w-[280px] shrink-0 rounded-[14px] bg-muted/50 p-2">
      <div className="mb-2 px-1">
        <div className="flex items-center gap-2">
          <span className={cn('size-2 rounded-full', STAGE_DOT[stage.color] ?? 'bg-blue-500')} />
          <span className="me-auto text-sm font-bold">{stage.name}</span>
          <span className="font-mono text-xs text-muted-foreground">{totals?.count ?? 0}</span>
        </div>
        <div className="font-mono text-xs" dir="ltr">
          <span className="font-bold text-blue-600 dark:text-blue-400">{wholeDollars(totals?.weighted ?? '0.00')}</span>
          <span className="text-muted-foreground"> / {wholeDollars(totals?.raw ?? '0.00')}</span>
        </div>
      </div>
      <div className="grid gap-2">
        {(column.data?.opportunities ?? []).map((opp) => (
          <DealCard
            key={opp.id}
            opp={opp}
            selected={selectedId === opp.id}
            onSelect={() => onSelect(opp.id)}
            isLastStage={isLastStage}
            onAdvance={() => onAdvance(opp.id)}
            onWon={() => onWon(opp)}
            onLost={() => onLost(opp)}
          />
        ))}
      </div>
      {range.pages > 1 ? (
        <div className="mt-2">
          <Pager
            page={paging.page}
            pages={range.pages}
            first={range.first}
            last={range.last}
            total={range.total}
            onPage={paging.setPage}
            label={stage.name}
          />
        </div>
      ) : null}
    </div>
  );
}

/** The board's two fixed ends — Closed Won and Closed Lost, each a page. */
function ClosedColumn({
  status,
  total,
  onSelect,
}: {
  status: 'won' | 'lost';
  total: number;
  onSelect: (id: string) => void;
}) {
  const { t } = useTranslation();
  const paging = usePagedState(BOARD_PAGE_SIZE, `board-${status}`);
  const column = useOpportunities({ status }, paging.page, BOARD_PAGE_SIZE);
  const range = usePageRange(column.data, BOARD_PAGE_SIZE);
  const rows = column.data?.opportunities ?? [];

  return (
    <div
      className={cn(
        'w-[280px] shrink-0 rounded-[14px] p-2',
        status === 'won' ? 'bg-emerald-50' : 'bg-red-50',
      )}
    >
      <div className="mb-2 flex items-center gap-2 px-1">
        <span
          className={cn('size-2 rounded-full', status === 'won' ? 'bg-emerald-500' : 'bg-red-500')}
        />
        <span className="me-auto text-sm font-bold">
          {t(status === 'won' ? 'crm.closedWon' : 'crm.closedLost')}
        </span>
        {/* The count is `/stats`', so it is the count of the STATUS and not
            the count of the page. */}
        <span className="font-mono text-xs text-muted-foreground">{total}</span>
      </div>
      <div className="grid gap-2">
        {rows.length === 0 ? (
          <p className="px-1 text-xs text-muted-foreground">{t('crm.noneYet')}</p>
        ) : (
          rows.map((opp) => (
            <button
              key={opp.id}
              type="button"
              onClick={() => onSelect(opp.id)}
              className="rounded-md border bg-background p-2 text-start text-sm shadow-sm"
            >
              <div className="font-bold">{opp.name}</div>
              <div className="font-mono text-xs" dir="ltr">
                {wholeDollars(opp.value)}
              </div>
              {opp.status === 'won' && (opp.wonOrderCode ?? opp.wonQuotationCode) ? (
                <div className="mt-1 text-xs font-semibold text-emerald-700 dark:text-emerald-400" dir="ltr">
                  {opp.wonDocumentType === 'sales_order'
                    ? t('crm.docSalesOrder')
                    : t('crm.docQuotation')}{' '}
                  {opp.wonOrderCode ?? opp.wonQuotationCode}
                </div>
              ) : null}
              {opp.status === 'lost' && opp.lossReason ? (
                <div className="mt-1 text-xs text-red-700 dark:text-red-400">
                  {t('crm.lostPrefix')} {t(LOSS_KEYS[opp.lossReason])}
                </div>
              ) : null}
            </button>
          ))
        )}
      </div>
      {range.pages > 1 ? (
        <div className="mt-2">
          <Pager
            page={paging.page}
            pages={range.pages}
            first={range.first}
            last={range.last}
            total={range.total}
            onPage={paging.setPage}
            label={t(status === 'won' ? 'crm.closedWon' : 'crm.closedLost')}
          />
        </div>
      ) : null}
    </div>
  );
}

export function OpportunitiesPage() {
  const { t } = useTranslation();
  const [params, setParams] = useSearchParams();
  const canEdit = useCan('crm.edit');
  const canConfigure = useCan('crm.approve');

  const [view, setView] = useState<'board' | 'table' | 'stalled'>('board');
  const [search, setSearch] = useState('');
  const [selectedId, setSelectedId] = useState<string | undefined>(
    params.get('selected') ?? undefined,
  );
  const [formOpen, setFormOpen] = useState(false);
  const [configOpen, setConfigOpen] = useState(false);
  const [wonTarget, setWonTarget] = useState<OpportunityResponse | null>(null);
  const [lostTarget, setLostTarget] = useState<OpportunityResponse | null>(null);
  const [error, setError] = useState('');

  useEffect(() => {
    document.title = pageTitle(t('crm.oppsTitle'));
  }, [t]);

  // Arriving from a conversion selects the new deal (CRM-01 §7's jump).
  useEffect(() => {
    const fromConvert = params.get('selected');
    if (fromConvert) {
      setSelectedId(fromConvert);
      setParams({}, { replace: true });
    }
  }, [params, setParams]);

  /**
   * TRUE: the board must keep drawing a switched-off stage while deals sit in
   * it, and the configuration panel must see it to switch it back on
   * (BR-DATA-06's "must keep showing"). The PICKERS below narrow to
   * `activeStages`, which is the same rule's other half.
   */
  const { data: stages } = useStages(true);
  const activeStages = useMemo(() => (stages ?? []).filter((stage) => stage.isActive), [stages]);
  /**
   * NFR-42 — the table and the Stalled view are one paged read of the same
   * route, carrying the search and the stalled chip as SERVER filters. Both
   * used to be a `.filter()` over every deal in the tenant, and the search
   * used to be a `.join(' ').includes()` over three fields; `q` matches the
   * same three in SQL (name, customer, code).
   */
  const tablePaging = usePagedState(TABLE_PAGE_SIZE, 'table');
  const listQuery = useMemo(
    () => ({
      ...(search.trim() ? { q: search.trim() } : {}),
      ...(view === 'stalled' ? { stalled: 'true' as const } : {}),
    }),
    [search, view],
  );
  const listed = useOpportunities(listQuery, tablePaging.page, TABLE_PAGE_SIZE);
  const filtered = listed.data?.opportunities ?? [];
  const listRange = usePageRange(listed.data, TABLE_PAGE_SIZE);
  /**
   * The five figures §5 prints about EVERY deal. They were five folds in this
   * component over the whole list, and a page would have made each of them
   * describe the twenty-five rows on screen (PAGINATION.md §3 step 10).
   */
  const { data: stats } = useOpportunityStats();
  const { data: selected } = useOpportunity(selectedId);
  const advance = useAdvanceOpportunity();
  const setStage = useSetOpportunityStage();
  const setProbability = useSetProbability();
  const closeWon = useCloseWon();
  const closeLost = useCloseLost();
  const logActivity = useLogOppActivity();
  const updateOpp = useUpdateOpportunity();

  const stalledCount = stats?.stalled ?? 0;

  // BR-CRM-02-06: the headline counts OPEN deals only — summed by the server
  // over every open deal, not by this component over a page of them.
  const weightedPipeline = stats?.open.weighted ?? '0.00';
  const rawPipeline = stats?.open.raw ?? '0.00';
  const openCount = stats?.open.count ?? 0;

  const act = (promise: Promise<unknown>) => {
    setError('');
    promise.catch((cause: unknown) => {
      // The cause reaches the decision — a refusal keeps the server's sentence
      // localized, a request that never answered gets this screen's own.
      setError(localizeServerCause(t, cause, 'crm.errSaveFailedOpportunity'));
    });
  };

  // Win rate excludes open deals (BR-CRM-02-19); the cycle reads the REAL
  // closed-on date (Q-CRM-02-3), won deals with a positive span only. Both are
  // counted over every deal by `/stats` — the RATE stays here, because a rate
  // already rounded on the wire cannot produce the two counts beside it.
  const won = stats?.won ?? 0;
  const lost = stats?.lost ?? 0;
  const decided = won + lost;
  const winRate = decided === 0 ? null : Math.round((won / decided) * 100);
  const avgCycle = stats?.avgCycleDays ?? null;

  return (
    <div className="grid gap-4">
      <CrmTabs />
      <div className="flex flex-wrap items-center gap-3">
        <div className="me-auto">
          <div className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground">
            {t('crm.eyebrow')}
          </div>
          <h2 className="text-lg font-bold">{t('crm.oppsTitle')}</h2>
        </div>
        <Gated permission="crm.create" reasonKey="perm.cannot.createOpportunities">
          <Button onClick={() => setFormOpen((value) => !value)}>{t('crm.newOpp')}</Button>
        </Gated>
      </div>

      {error ? (
        <div role="alert" className="rounded-md border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-700">
          {error}
        </div>
      ) : null}

      {/* The pipeline headline — weighted loudest, open deals only. */}
      <div className="grid gap-3 sm:grid-cols-3">
        <Card className="border-blue-300 bg-blue-50/60">
          <CardContent className="p-3">
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <ChartColumn className="size-4 text-blue-600 dark:text-blue-400" aria-hidden />
              {t('crm.weightedPipeline')}
            </div>
            <div className="font-mono text-3xl font-bold" dir="ltr">
              {wholeDollars(weightedPipeline)}
            </div>
            <div className="text-[11px] text-muted-foreground" dir="ltr">
              Σ value × probability
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-3">
            <div className="text-xs text-muted-foreground">{t('crm.rawPipeline')}</div>
            <div className="font-mono text-2xl font-bold text-muted-foreground" dir="ltr">
              {wholeDollars(rawPipeline)}
            </div>
            <div className="text-[11px] text-muted-foreground">{t('crm.rawPipelineSub')}</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-3 text-center">
            <div className="font-mono text-3xl font-bold">{openCount}</div>
            <div className="text-xs text-muted-foreground">{t('crm.openDeals')}</div>
          </CardContent>
        </Card>
      </div>

      {/* The stalled banner (BR-CRM-02-17) — shown to every reader. */}
      {stalledCount > 0 ? (
        <div className="flex flex-wrap items-center gap-3 rounded-md border border-amber-300 bg-amber-50 px-4 py-3">
          <TriangleAlert className="size-6 text-amber-700 dark:text-amber-400" aria-hidden />
          <div className="me-auto">
            <div className="text-sm font-bold text-amber-800 dark:text-amber-400">
              {t('crm.stalledBanner', { count: stalledCount })}
            </div>
            <div className="text-xs text-amber-700 dark:text-amber-400">{t('crm.stalledBannerSub')}</div>
          </div>
          <Button size="sm" className="bg-amber-600 hover:bg-amber-700" onClick={() => tablePaging.change(setView)('stalled')}>
            {t('crm.viewStalled')} <ArrowRight aria-hidden />
          </Button>
        </div>
      ) : null}

      {/* Toolbar. */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="me-auto flex items-center gap-1">
          {(['board', 'table'] as const).map((name) => (
            <Button
              key={name}
              size="sm"
              variant={view === name ? 'default' : 'outline'}
              onClick={() => tablePaging.change(setView)(name)}
            >
              {t(name === 'board' ? 'crm.viewBoard' : 'crm.viewTable')}
            </Button>
          ))}
          <Button
            size="sm"
            variant="outline"
            className={cn(
              'border-amber-300 text-amber-700 dark:text-amber-400',
              view === 'stalled' && 'bg-amber-700 text-white hover:bg-amber-800',
            )}
            onClick={() => tablePaging.change(setView)('stalled')}
          >
            {t('crm.viewStalledTab', { count: stalledCount })}
          </Button>
          <Button
            size="sm"
            variant="outline"
            disabled={!canConfigure}
            title={canConfigure ? undefined : t('crm.noConfigPermission')}
            onClick={() => setConfigOpen((value) => !value)}
          >
            {t('crm.configureStages')}
          </Button>
        </div>
        <div className="relative w-full max-w-[250px]">
          <Search
            className="pointer-events-none absolute start-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground"
            aria-hidden
          />
          <Input
            aria-label={t('crm.searchOpps')}
            placeholder={t('crm.searchOppsPh')}
            className="ps-9"
            value={search}
            onChange={(event) => tablePaging.change(setSearch)(event.target.value)}
          />
        </div>
      </div>

      {configOpen && canConfigure ? <StageConfig stages={stages ?? []} onError={setError} /> : null}
      {formOpen ? (
        <NewDealForm
          /* BR-DATA-06: a new deal is never offered a switched-off stage. */
          stages={activeStages}
          onClose={() => setFormOpen(false)}
          onCreated={(id) => {
            setFormOpen(false);
            setSelectedId(id);
          }}
        />
      ) : null}

      {/* Board: the configured stages + the two fixed ends. Each column is
          its OWN paged read of the same route carrying its own stage, exactly
          as CRM-01's five status columns are — BR-CRM-02-21 holds, because
          they all read the one configured stage list. */}
      {view === 'board' ? (
        <div className="flex gap-3 overflow-x-auto pb-2">
          {(stages ?? []).map((stage) => (
            <StageColumn
              key={stage.id}
              stage={stage}
              totals={stats?.byStage.find((row) => row.stageId === stage.id)}
              /* Advance stops at the last ACTIVE stage, because that is the
                 last one it can reach (`OpportunitiesService.advance`). */
              isLastStage={stage.position >= (activeStages.at(-1)?.position ?? 0)}
              selectedId={selectedId}
              onSelect={setSelectedId}
              onAdvance={(id) => act(advance.mutateAsync(id))}
              onWon={setWonTarget}
              onLost={setLostTarget}
            />
          ))}
          {(['won', 'lost'] as const).map((status) => (
            <ClosedColumn
              key={status}
              status={status}
              total={status === 'won' ? won : lost}
              onSelect={setSelectedId}
            />
          ))}
        </div>
      ) : (
        <Card>
          <CardContent className="overflow-x-auto">
            <Table className="min-w-[820px]">
              <TableHeader>
                <TableRow>
                  <TableHead>{t('crm.colCode')}</TableHead>
                  <TableHead>{t('crm.colDeal')}</TableHead>
                  <TableHead>{t('crm.colCustomer')}</TableHead>
                  <TableHead className="text-end">{t('crm.colValue')}</TableHead>
                  <TableHead>{t('crm.colStage')}</TableHead>
                  <TableHead className="text-end">{t('crm.colProbability')}</TableHead>
                  <TableHead className="text-end">{t('crm.colWeighted')}</TableHead>
                  <TableHead>{t('crm.colActions')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} className="py-8 text-center text-muted-foreground">
                      {t('crm.noOpps')}
                    </TableCell>
                  </TableRow>
                ) : (
                  filtered.map((opp) => (
                    <TableRow
                      key={opp.id}
                      onClick={() => setSelectedId(opp.id)}
                      className={cn('cursor-pointer', opp.stalled && 'bg-amber-50/60')}
                    >
                      <TableCell>
                        <Ltr value={opp.code} className="font-mono text-muted-foreground" />
                        {opp.fromLeadCode ? (
                          <div className="text-[10px] text-muted-foreground" dir="ltr">
                            {t('crm.fromLead')} {opp.fromLeadCode}
                          </div>
                        ) : null}
                      </TableCell>
                      <TableCell className="font-medium">{opp.name}</TableCell>
                      <TableCell>{opp.customerName}</TableCell>
                      <TableCell className="text-end font-mono" dir="ltr">
                        {wholeDollars(opp.value)}
                      </TableCell>
                      <TableCell>
                        {opp.status === 'open' ? (
                          opp.stageName
                        ) : (
                          <span className={opp.status === 'won' ? 'font-semibold text-emerald-700 dark:text-emerald-400' : 'font-semibold text-red-700 dark:text-red-400'}>
                            {t(opp.status === 'won' ? 'crm.closedWon' : 'crm.closedLost')}
                          </span>
                        )}
                        {opp.stalled ? (
                          <TriangleAlert className="ms-1 inline size-3 text-amber-700 dark:text-amber-400" aria-label={t('crm.stalled')} />
                        ) : null}
                      </TableCell>
                      <TableCell className="text-end font-mono" dir="ltr">
                        {t('unit.percent', { value: opp.probability })}{opp.manualOverride ? ' ✎' : ''}
                      </TableCell>
                      <TableCell className="text-end font-mono" dir="ltr">
                        {wholeDollars(opp.weightedValue)}
                      </TableCell>
                      <TableCell onClick={(event) => event.stopPropagation()}>
                        {opp.status === 'open' ? (
                          <>
                            {((stages ?? []).find((stage) => stage.id === opp.stageId)?.position ??
                              0) < (activeStages.at(-1)?.position ?? 0) ? (
                              // BR-CRM-02-27: Advance in the table's action column.
                              <Gated permission="crm.edit" reasonKey="perm.cannot.editOpportunities">
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => act(advance.mutateAsync(opp.id))}
                              >
                                {t('crm.advance')}
                              </Button>
                              </Gated>
                            ) : null}
                            <Gated permission="crm.edit" reasonKey="perm.cannot.editOpportunities">
                              <Button
                                size="sm"
                                variant="outline"
                                className="ms-1 text-emerald-700 dark:text-emerald-400"
                                onClick={() => setWonTarget(opp)}
                              >
                                {t('crm.won')}
                              </Button>
                            </Gated>
                            <Gated permission="crm.edit" reasonKey="perm.cannot.editOpportunities">
                              <Button
                                size="sm"
                                variant="outline"
                                className="ms-1 text-red-600 dark:text-red-400"
                                onClick={() => setLostTarget(opp)}
                              >
                                {t('crm.lost')}
                              </Button>
                            </Gated>
                          </>
                        ) : opp.status === 'won' ? (
                          <WonDocLink opp={opp} />
                        ) : opp.lossReason ? (
                          <span className="text-xs text-muted-foreground">
                            {t(LOSS_KEYS[opp.lossReason])}
                          </span>
                        ) : null}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
            {/* NFR-42 — the range, the total, and the two steps. The total is
                the SEARCHED total, so "Showing 1–25 of 312" describes the
                matches and not the tenant. */}
            {(listed.data?.total ?? 0) > 0 ? (
              <Pager
                page={tablePaging.page}
                pages={listRange.pages}
                first={listRange.first}
                last={listRange.last}
                total={listRange.total}
                onPage={tablePaging.setPage}
                label={t('crm.oppsTitle')}
              />
            ) : null}
          </CardContent>
        </Card>
      )}

      {/* Detail + pipeline-by-stage. */}
      <div className="grid gap-4 lg:grid-cols-[2fr_1fr]">
        <Card>
          <CardContent className="grid gap-3">
            {selected ? (
              <>
                <div className="flex flex-wrap items-center gap-2">
                  <h3 className="me-auto text-base font-bold">{selected.name}</h3>
                  {selected.fromLeadCode ? (
                    <span className="rounded-full bg-muted px-2 py-0.5 font-mono text-xs" dir="ltr">
                      {t('crm.fromLead')} {selected.fromLeadCode}
                    </span>
                  ) : null}
                  {selected.status === 'won' ? <WonDocLink opp={selected} /> : null}
                </div>
                <div className="grid grid-cols-3 gap-2">
                  <div className="rounded-md border p-2">
                    <div className="text-xs text-muted-foreground">{t('crm.dValue')}</div>
                    <div className="font-mono text-lg font-bold" dir="ltr">
                      {wholeDollars(selected.value)}
                    </div>
                  </div>
                  <div className="rounded-md border border-blue-300 bg-blue-50/60 p-2">
                    <div className="text-xs text-muted-foreground">{t('crm.dWeighted')}</div>
                    <div className="font-mono text-lg font-bold" dir="ltr">
                      {wholeDollars(selected.weightedValue)}
                    </div>
                  </div>
                  <div className="rounded-md border p-2">
                    <div className="text-xs text-muted-foreground">
                      {t('crm.dProbability')} —{' '}
                      {selected.manualOverride ? t('crm.manualOverride') : t('crm.autoFromStage')}
                    </div>
                    <div className="flex items-center gap-1">
                      <Input
                        aria-label={t('crm.dProbability')}
                        className="h-8 w-20 font-mono"
                        dir="ltr"
                        inputMode="numeric"
                        disabled={selected.status !== 'open' || !canEdit}
                        defaultValue={String(selected.probability)}
                        key={`${selected.id}-${selected.probability}`}
                        onBlur={(event) => {
                          const typed = Math.max(0, Math.min(100, Number(event.target.value) || 0));
                          if (typed !== selected.probability) {
                            act(
                              setProbability.mutateAsync({
                                opportunityId: selected.id,
                                body: { probability: typed },
                              }),
                            );
                          }
                        }}
                      />
                      <span className="text-sm">{t('unit.percentSign')}</span>
                      {selected.manualOverride && selected.status === 'open' ? (
                        <Gated permission="crm.edit" reasonKey="perm.cannot.editOpportunities">
                        <Button
                          size="sm"
                          variant="outline"
                          title={t('crm.resetToDefault')}
                          onClick={() =>
                            act(
                              setProbability.mutateAsync({
                                opportunityId: selected.id,
                                body: { reset: true },
                              }),
                            )
                          }
                        >
                          ↺
                        </Button>
                        </Gated>
                      ) : null}
                    </div>
                  </div>
                </div>
                {selected.status === 'open' ? (
                  <div className="flex flex-wrap items-center gap-2">
                    <Label htmlFor="deal-stage">{t('crm.colStage')}</Label>
                    <NativeSelect
                      id="deal-stage"
                      className="w-auto"
                      value={selected.stageId}
                      disabled={!canEdit}
                      onChange={(event) =>
                        act(
                          setStage.mutateAsync({
                            opportunityId: selected.id,
                            stageId: event.target.value,
                          }),
                        )
                      }
                    >
                      {/* BR-DATA-06: only active stages are offered as a
                          DESTINATION — plus the deal's OWN stage when it has
                          since been switched off, so the select can still
                          show where the deal actually is (§15's "deals stay
                          put"). Re-picking it is the no-op `setStage`
                          short-circuits on. */}
                      {(stages ?? [])
                        .filter((stage) => stage.isActive || stage.id === selected.stageId)
                        .map((stage) => (
                          <option key={stage.id} value={stage.id}>
                            {stage.name}
                          </option>
                        ))}
                    </NativeSelect>
                    <Label htmlFor="deal-value" className="ms-2">
                      {t('crm.dValue')}
                    </Label>
                    <Input
                      id="deal-value"
                      className="w-28 font-mono"
                      dir="ltr"
                      inputMode="numeric"
                      disabled={!canEdit}
                      defaultValue={selected.value}
                      key={`value-${selected.id}-${selected.value}`}
                      onBlur={(event) => {
                        const typed = event.target.value.replace(/[^\d.]/g, '');
                        if (typed && typed !== selected.value) {
                          act(
                            updateOpp.mutateAsync({
                              opportunityId: selected.id,
                              body: { value: typed },
                            }),
                          );
                        }
                      }}
                    />
                  </div>
                ) : null}
                {selected.items.length > 0 ? (
                  <div className="flex flex-wrap gap-1">
                    {selected.items.map((item) => (
                      <span key={item.itemId} className="rounded-full bg-blue-50 px-2 py-0.5 text-xs text-blue-800">
                        {item.name}
                      </span>
                    ))}
                  </div>
                ) : null}
                {selected.status === 'open' ? (
                  <OppComposer
                    disabled={!canEdit}
                    onSubmit={(kind, body) =>
                      act(
                        logActivity.mutateAsync({
                          opportunityId: selected.id,
                          body: { kind, body },
                        }),
                      )
                    }
                  />
                ) : null}
                <div className="grid gap-2" data-testid="opp-activity-log">
                  {selected.activities.map((entry) => (
                    <div key={entry.id} className="rounded-md border px-3 py-2 text-sm">
                      <div className="flex items-center gap-2 text-xs text-muted-foreground">
                        <span className="font-semibold uppercase">{t(`crm.act_${entry.kind}`)}</span>
                        {entry.createdByName ? <span>{entry.createdByName}</span> : null}
                        <Ltr value={entry.createdAt.slice(0, 16).replace('T', ' ')} className="font-mono" />
                      </div>
                      <div>{entry.body}</div>
                    </div>
                  ))}
                </div>
                <TaskPanel
                  relatedKind="opportunity"
                  relatedId={selected.id}
                  relatedKey={selected.code}
                  relatedName={selected.customerName}
                />
              </>
            ) : (
              <p className="py-8 text-center text-sm text-muted-foreground">{t('crm.selectOpp')}</p>
            )}
          </CardContent>
        </Card>

        <div className="grid gap-4 content-start">
          <Card>
            <CardContent className="grid gap-2">
              <h3 className="text-base font-bold">{t('crm.pipelineByStage')}</h3>
              {(() => {
                // §5's panel is a figure about EVERY open deal per stage, so
                // it reads `/stats` rather than folding the rows on screen —
                // which after paging would be one page of one column.
                const rows = (stages ?? []).map((stage) => {
                  const totals = stats?.byStage.find((row) => row.stageId === stage.id);
                  return {
                    stage,
                    count: totals?.count ?? 0,
                    weighted: totals?.weighted ?? '0.00',
                    raw: totals?.raw ?? '0.00',
                  };
                });
                // A PIXEL width, not an amount: the bar is drawn as a share of
                // the widest column. The amounts stay decimal strings above;
                // only the ratio that becomes a CSS percentage is a number.
                const maxRaw = Math.max(1, ...rows.map((row) => chartValue(row.raw)));
                return rows.map((row) => (
                  <div key={row.stage.id} className="grid gap-1 text-sm">
                    <div className="flex items-center justify-between gap-2">
                      <span>
                        {row.stage.name} · {row.count}
                      </span>
                      <span className="font-mono text-xs" dir="ltr">
                        <span className="font-bold text-blue-600 dark:text-blue-400">{wholeDollars(row.weighted)}</span>
                        <span className="text-muted-foreground"> / {wholeDollars(row.raw)}</span>
                      </span>
                    </div>
                    <div className="h-1.5 w-full rounded bg-muted">
                      <div
                        className={cn('h-1.5 rounded', STAGE_DOT[row.stage.color] ?? 'bg-blue-500')}
                        style={{ width: `${barPercent(row.raw, maxRaw)}%` }}
                      />
                    </div>
                  </div>
                ));
              })()}
            </CardContent>
          </Card>

          <Card>
            <CardContent className="grid gap-2">
              <h3 className="text-base font-bold">{t('crm.forecastTitle')}</h3>
              <div className="grid grid-cols-2 gap-2 text-center">
                <div className="rounded-md border p-2">
                  <div className="font-mono text-2xl font-bold text-emerald-700 dark:text-emerald-400">
                    {winRate === null ? '—' : <Pct value={winRate} />}
                  </div>
                  <div className="text-xs text-muted-foreground">{t('crm.winRate')}</div>
                  <div className="text-[11px] text-muted-foreground">
                    {t('crm.wonLostCounts', { won, lost })}
                  </div>
                </div>
                <div className="rounded-md border p-2">
                  <div className="font-mono text-2xl font-bold">{avgCycle ?? '—'}</div>
                  <div className="text-xs text-muted-foreground">{t('crm.avgCycle')}</div>
                  <div className="text-[11px] text-muted-foreground">{t('crm.avgCycleSub')}</div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Close Won — generate document (BR-CRM-02-12, BR-CRM-02-13, BR-CRM-02-14). */}
      {wonTarget ? (
        <CloseWonModal
          opp={wonTarget}
          onClose={() => setWonTarget(null)}
          onConfirm={(documentType) =>
            act(
              closeWon
                .mutateAsync({ opportunityId: wonTarget.id, body: { documentType } })
                .then(() => setWonTarget(null)),
            )
          }
        />
      ) : null}

      {/* Close Lost — reason required (BR-CRM-02-15). */}
      {lostTarget ? (
        <CloseLostModal
          opp={lostTarget}
          onClose={() => setLostTarget(null)}
          onConfirm={(reason, note) =>
            act(
              closeLost
                .mutateAsync({
                  opportunityId: lostTarget.id,
                  body: { reason, ...(note ? { note } : {}) },
                })
                .then(() => setLostTarget(null)),
            )
          }
        />
      ) : null}
    </div>
  );
}

function DealCard({
  opp,
  selected,
  isLastStage,
  onSelect,
  onAdvance,
  onWon,
  onLost,
}: {
  opp: OpportunityResponse;
  selected: boolean;
  isLastStage: boolean;
  onSelect: () => void;
  onAdvance: () => void;
  onWon: () => void;
  onLost: () => void;
}) {
  const { t } = useTranslation();
  return (
    <div
      className={cn(
        'rounded-md border bg-background p-2 text-sm shadow-sm',
        opp.stalled && 'border-s-4 border-s-amber-500 bg-amber-50/40',
        selected && 'ring-2 ring-primary',
      )}
    >
      <button type="button" className="w-full text-start" onClick={onSelect}>
        <div className="font-bold">{opp.name}</div>
        <div className="flex items-center justify-between">
          <span className="text-xs text-muted-foreground">{opp.customerName}</span>
          <span className="font-mono text-xs font-bold" dir="ltr">
            {wholeDollars(opp.value)}
          </span>
        </div>
        <div className="mt-1 flex flex-wrap items-center gap-1">
          <span className="rounded-full bg-blue-100 px-2 py-0.5 font-mono text-[11px] text-blue-800" dir="ltr">
            {t('unit.percent', { value: opp.probability })}{opp.manualOverride ? ' ✎' : ''}
          </span>
          {opp.fromLeadCode ? (
            <span className="rounded-full bg-muted px-2 py-0.5 font-mono text-[10px]" dir="ltr">
              {opp.fromLeadCode}
            </span>
          ) : null}
          {opp.stalled ? (
            <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-semibold text-amber-800">
              {opp.stalledReason === 'close_date_passed'
                ? t('crm.closeDatePassed')
                : t('crm.idleDays', { count: opp.idleDays })}
            </span>
          ) : null}
        </div>
      </button>
      {/* These three were `disabled` and SILENT — the shape 16.7 rules out as
          firmly as hidden. The card's own `canEdit` is gone; <GatedGroup>
          greys the row and carries the sentence.

          BR-CRM-02-11 / ERR-CRM-02-04: the board's route to Won or Lost opens
          the MATCHING MODAL and changes nothing — Won and Lost here only set
          the modal's target, and the sole write is the modal's own Confirm.
          The prototype's gesture was a card drag; this board's affordance is
          the card's buttons, and the "quick move" the ERR row guards against
          has no code path at all: won/lost are statuses, not stage rows, so
          `setStage` refuses them and only closeWon/closeLost can end a deal. */}
      <GatedGroup permission="crm.edit" reasonKey="perm.cannot.editOpportunities">
      <div className="mt-1 flex gap-1">
        {!isLastStage ? (
          <Button size="sm" variant="outline" onClick={onAdvance}>
            {t('crm.advance')}
          </Button>
        ) : null}
        <Button size="sm" variant="outline" className="text-emerald-700 dark:text-emerald-400" onClick={onWon}>
          {t('crm.won')}
        </Button>
        <Button size="sm" variant="outline" className="text-red-600 dark:text-red-400" onClick={onLost}>
          {t('crm.lost')}
        </Button>
      </div>
      </GatedGroup>
    </div>
  );
}

function WonDocLink({ opp }: { opp: OpportunityResponse }) {
  const { t } = useTranslation();
  const code = opp.wonOrderCode ?? opp.wonQuotationCode;
  if (code === null) return null;
  const href =
    opp.wonDocumentType === 'sales_order'
      ? `/sales/orders/${opp.wonOrderId}/edit`
      : `/sales/quotes/${opp.wonQuotationId}`;
  return (
    <Link to={href} className="text-xs font-semibold text-emerald-700 dark:text-emerald-400 underline" dir="ltr">
      {opp.wonDocumentType === 'sales_order' ? t('crm.docSalesOrder') : t('crm.docQuotation')} {code}
    </Link>
  );
}

/** The new-deal form — Create disabled until name + customer + value > 0 (BR-CRM-02-01). */
function NewDealForm({
  stages,
  onClose,
  onCreated,
}: {
  stages: OppStageResponse[];
  onClose: () => void;
  onCreated: (id: string) => void;
}) {
  const { t } = useTranslation();
  const create = useCreateOpportunity();
  const [name, setName] = useState('');
  const [partnerId, setPartnerId] = useState('');
  const [customerName, setCustomerName] = useState('');
  const [value, setValue] = useState('');
  const [stageId, setStageId] = useState(stages[0]?.id ?? '');
  const [expectedClose, setExpectedClose] = useState('');
  const [itemIds, setItemIds] = useState<string[]>([]);
  const [error, setError] = useState('');

  // GEN-50 — the customer field is a typeahead; the item strip below is a
  // CHIP control (CRM-02 §5), so it keeps its shape and gains the two
  // bounded reads: one page of items, plus whichever this opportunity holds.
  const partnerPicker = usePartnerPicker(partnerId || null, { type: 'customer' });
  const itemPicker = useItemPicker(itemIds, { status: 'active' });
  const items = useMemo(() => {
    const shown = new Map<string, { id: string; name: string }>();
    // The chosen ones first, so a saved item outside the current page still
    // draws as chosen rather than quietly unticking itself.
    for (const id of itemIds) {
      const held = itemPicker.byId.get(id);
      if (held) shown.set(id, { id, name: held.name });
    }
    for (const row of itemPicker.options) shown.set(row.id, { id: row.id, name: row.label });
    return [...shown.values()];
  }, [itemIds, itemPicker]);


  const complete =
    name.trim().length > 0 &&
    (partnerId !== '' || customerName.trim().length > 0) &&
    Number(value) > 0 &&
    stageId !== '';

  return (
    <Card className="border-primary">
      <CardContent className="grid gap-3">
        <h3 className="text-base font-bold">{t('crm.newOpp')}</h3>
        {error ? (
          <div role="alert" className="rounded-md border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-700">
            {error}
          </div>
        ) : null}
        <div className="grid gap-3 sm:grid-cols-2">
          <div className="grid gap-1">
            <Label htmlFor="opp-name">{t('crm.fDealName')}</Label>
            <Input id="opp-name" value={name} onChange={(event) => setName(event.target.value)} />
          </div>
          <div className="grid gap-1">
            <Label htmlFor="opp-partner">{t('crm.fCustomer')}</Label>
            <PartnerTypeahead
              entity="customer"
              id="opp-partner"
              label={t('crm.fCustomer')}
              value={partnerId || null}
              onChange={(id) => setPartnerId(id ?? '')}
              options={partnerPicker.options}
              selected={partnerPicker.optionFor(partnerId)}
              query={partnerPicker.query}
              onQueryChange={partnerPicker.setQuery}
              clearLabel={t('crm.freeTextCustomer')}
              emptyLabel={t('picker.noMatchingCustomer')}
              loading={partnerPicker.loading}
            />
            {partnerId === '' ? (
              <Input
                aria-label={t('crm.fCustomerName')}
                placeholder={t('crm.fCustomerName')}
                value={customerName}
                onChange={(event) => setCustomerName(event.target.value)}
              />
            ) : null}
          </div>
          <div className="grid gap-1">
            <Label htmlFor="opp-value">{t('crm.fValue')}</Label>
            <Input
              id="opp-value"
              inputMode="numeric"
              dir="ltr"
              className="font-mono"
              value={value}
              onChange={(event) => setValue(event.target.value.replace(/[^\d.]/g, ''))}
            />
          </div>
          <div className="grid gap-1">
            <Label htmlFor="opp-stage">{t('crm.fStage')}</Label>
            {/* BR-CRM-02-04: only the configured stages are offered. */}
            <NativeSelect
              id="opp-stage"
              value={stageId}
              onChange={(event) => setStageId(event.target.value)}
            >
              {stages.map((stage) => (
                <option key={stage.id} value={stage.id}>
                  {stage.name} · {t('unit.percent', { value: stage.probability })}
                </option>
              ))}
            </NativeSelect>
          </div>
          <div className="grid gap-1">
            <Label htmlFor="opp-close">{t('crm.fExpectedClose')}</Label>
            <Input
              id="opp-close"
              type="date"
              dir="ltr"
              value={expectedClose}
              onChange={(event) => setExpectedClose(event.target.value)}
            />
          </div>
        </div>
        {items.length > 0 ? (
          <div className="grid gap-1">
            <Label>{t('crm.fItems')}</Label>
            <div className="flex flex-wrap gap-1">
              {items.map((item) => {
                const active = itemIds.includes(item.id);
                return (
                  <button
                    key={item.id}
                    type="button"
                    className={cn(
                      'rounded-full border px-2 py-0.5 text-xs',
                      active ? 'border-blue-400 bg-blue-50 text-blue-800' : 'text-muted-foreground',
                    )}
                    onClick={() =>
                      setItemIds((current) =>
                        active ? current.filter((id) => id !== item.id) : [...current, item.id],
                      )
                    }
                  >
                    {active ? '✓ ' : ''}
                    {item.name}
                  </button>
                );
              })}
            </div>
          </div>
        ) : null}
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button
            disabled={!complete || create.isPending}
            onClick={() =>
              create
                .mutateAsync({
                  name: name.trim(),
                  ...(partnerId ? { partnerId } : { customerName: customerName.trim() }),
                  value: value.includes('.') ? value : `${value}`,
                  stageId,
                  ...(expectedClose ? { expectedClose } : {}),
                  ...(itemIds.length > 0 ? { itemIds } : {}),
                })
                .then((created) => onCreated(created.id))
                .catch((cause: unknown) => {
                  setError(localizeServerCause(t, cause, 'crm.errSaveFailedOpportunity'));
                })
            }
          >
            {t('crm.createOpp')}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

/**
 * Configure stages (crm.approve): rename, re-weight, reorder (§5c), and
 * BR-DATA-05's switch-off.
 *
 * This is the ONE list on the screen drawn from every stage including the
 * switched-off ones, because it is where a switched-off one is switched back
 * on — the same argument MD-11's currency table makes for its own maintenance
 * screen.
 */
function StageConfig({
  stages,
  onError,
}: {
  stages: OppStageResponse[];
  onError: (message: string) => void;
}) {
  const { t } = useTranslation();
  const updateStage = useUpdateStage();
  const setStageActive = useSetStageActive();
  const reorder = useReorderStages();

  const act = (promise: Promise<unknown>) => {
    onError('');
    promise.catch((cause: unknown) => {
      // This one reached the strip by a SECOND route: it never called
      // `localizeServerMessage` at all, so a refusal that arrived was printed
      // in English to an Arabic reader AND a request that never answered put
      // the browser's own `Failed to fetch` here. One call fixes both.
      onError(localizeServerCause(t, cause, 'crm.errSaveFailedOpportunity'));
    });
  };

  const move = (index: number, delta: -1 | 1) => {
    const order = stages.map((stage) => stage.id);
    const target = index + delta;
    if (target < 0 || target >= order.length) return;
    const swapped = [...order];
    [swapped[index], swapped[target]] = [swapped[target]!, swapped[index]!];
    act(reorder.mutateAsync({ stageIds: swapped }));
  };

  return (
    <Card>
      <CardContent className="grid gap-2">
        <h3 className="text-base font-bold">{t('crm.configureStages')}</h3>
        <p className="text-xs text-muted-foreground">{t('crm.configureHint')}</p>
        {stages.map((stage, index) => (
          <div key={stage.id} className="flex flex-wrap items-center gap-2">
            <span className={cn('size-2 rounded-full', STAGE_DOT[stage.color] ?? 'bg-blue-500')} />
            <Input
              aria-label={t('crm.stageName', { position: stage.position })}
              className="w-48"
              defaultValue={stage.name}
              key={`${stage.id}-${stage.name}`}
              onBlur={(event) => {
                if (event.target.value !== stage.name) {
                  act(updateStage.mutateAsync({ stageId: stage.id, body: { name: event.target.value } }));
                }
              }}
            />
            <Label className="text-xs text-muted-foreground">{t('crm.defaultPct')}</Label>
            <Input
              aria-label={t('crm.stagePct', { position: stage.position })}
              className="w-20 font-mono"
              dir="ltr"
              inputMode="numeric"
              defaultValue={String(stage.probability)}
              key={`${stage.id}-pct-${stage.probability}`}
              onBlur={(event) => {
                const typed = Math.max(0, Math.min(100, Number(event.target.value) || 0));
                if (typed !== stage.probability) {
                  act(updateStage.mutateAsync({ stageId: stage.id, body: { probability: typed } }));
                }
              }}
            />
            <span className="text-xs text-muted-foreground">
              {t('crm.openInStage', { count: stage.openDealCount })}
            </span>
            {stage.isActive ? null : (
              <span className="rounded-full bg-muted px-2 py-0.5 text-[11px] font-medium text-muted-foreground">
                {t('crm.stageInactive')}
              </span>
            )}
            <span className="ms-auto flex gap-1">
              <Button
                size="sm"
                variant="outline"
                onClick={() =>
                  act(setStageActive.mutateAsync({ stageId: stage.id, isActive: !stage.isActive }))
                }
              >
                {stage.isActive ? t('crm.stageSwitchOff') : t('crm.stageSwitchOn')}
              </Button>
              <Button
                size="sm"
                variant="outline"
                aria-label={t('crm.moveUp', { name: stage.name })}
                disabled={index === 0}
                onClick={() => move(index, -1)}
              >
                <ArrowUp className="size-3" aria-hidden />
              </Button>
              <Button
                size="sm"
                variant="outline"
                aria-label={t('crm.moveDown', { name: stage.name })}
                disabled={index === stages.length - 1}
                onClick={() => move(index, 1)}
              >
                <ArrowDown className="size-3" aria-hidden />
              </Button>
            </span>
          </div>
        ))}
      </CardContent>
    </Card>
  );
}

function OppComposer({
  disabled,
  onSubmit,
}: {
  disabled: boolean;
  onSubmit: (kind: 'note' | 'call' | 'email' | 'meeting', body: string) => void;
}) {
  const { t } = useTranslation();
  const [kind, setKind] = useState<'note' | 'call' | 'email' | 'meeting'>('note');
  const [body, setBody] = useState('');
  return (
    <div className="grid gap-2">
      <div className="flex flex-wrap items-center gap-1">
        {(['note', 'call', 'email', 'meeting'] as const).map((value) => (
          <Button
            key={value}
            type="button"
            size="sm"
            variant={kind === value ? 'default' : 'outline'}
            onClick={() => setKind(value)}
          >
            {t(`crm.act_${value}`)}
          </Button>
        ))}
      </div>
      <div className="flex gap-2">
        <Input
          aria-label={t('crm.logPlaceholder')}
          placeholder={t('crm.logPlaceholder')}
          value={body}
          onChange={(event) => setBody(event.target.value)}
        />
        <Button
          disabled={disabled || body.trim().length === 0}
          onClick={() => {
            onSubmit(kind, body.trim());
            setBody('');
          }}
        >
          {t('crm.addToLog')}
        </Button>
      </div>
    </div>
  );
}

function CloseWonModal({
  opp,
  onClose,
  onConfirm,
}: {
  opp: OpportunityResponse;
  onClose: () => void;
  onConfirm: (documentType: 'sales_order' | 'quotation') => void;
}) {
  const { t } = useTranslation();
  const [documentType, setDocumentType] = useState<'sales_order' | 'quotation'>('sales_order');
  return (
    <Modal title={t('crm.closeWonTitle')} onClose={onClose}>
      <div className="grid gap-3">
        <p className="text-sm text-muted-foreground">{t('crm.closeWonExplain')}</p>
        <div className="flex gap-2">
          {(['sales_order', 'quotation'] as const).map((kind) => (
            <Button
              key={kind}
              variant={documentType === kind ? 'default' : 'outline'}
              onClick={() => setDocumentType(kind)}
            >
              {t(kind === 'sales_order' ? 'crm.docSalesOrder' : 'crm.docQuotation')}
            </Button>
          ))}
        </div>
        <div className="grid gap-1 rounded-md border border-emerald-300 bg-emerald-50 p-3 text-sm">
          <div>
            <span className="text-muted-foreground">{t('crm.pvCustomer')}:</span> {opp.customerName}
          </div>
          <div>
            <span className="text-muted-foreground">{t('crm.pvItems')}:</span>{' '}
            {opp.items.length === 0 ? '—' : opp.items.map((item) => item.name).join(', ')}
          </div>
          <div>
            <span className="text-muted-foreground">{t('crm.pvValue')}:</span>{' '}
            <span className="font-mono" dir="ltr">
              {wholeDollars(opp.value)}
            </span>
          </div>
        </div>
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button className="bg-emerald-600 hover:bg-emerald-700" onClick={() => onConfirm(documentType)}>
            {t('crm.closeWonConfirm')}
          </Button>
        </div>
      </div>
    </Modal>
  );
}

function CloseLostModal({
  opp,
  onClose,
  onConfirm,
}: {
  opp: OpportunityResponse;
  onClose: () => void;
  onConfirm: (reason: OppLossReason, note: string) => void;
}) {
  const { t } = useTranslation();
  const [reason, setReason] = useState<'' | OppLossReason>('');
  const [note, setNote] = useState('');
  return (
    <Modal title={t('crm.closeLostTitle')} onClose={onClose}>
      <div className="grid gap-3">
        <p className="text-sm text-muted-foreground">{opp.name}</p>
        <div className="grid gap-1">
          <Label htmlFor="lost-reason">{t('crm.dqReason')}</Label>
          <NativeSelect
            id="lost-reason"
            value={reason}
            onChange={(event) => setReason(event.target.value as '' | OppLossReason)}
          >
            <option value="">{t('crm.chooseReason')}</option>
            {OPP_LOSS_REASONS.map((value) => (
              <option key={value} value={value}>
                {t(LOSS_KEYS[value])}
              </option>
            ))}
          </NativeSelect>
        </div>
        <div className="grid gap-1">
          <Label htmlFor="lost-note">{t('crm.dqNote')}</Label>
          <Input id="lost-note" value={note} onChange={(event) => setNote(event.target.value)} />
        </div>
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          {/* BR-CRM-02-15: disabled until a reason is chosen. */}
          <Button
            variant="destructive"
            disabled={reason === ''}
            onClick={() => onConfirm(reason as OppLossReason, note.trim())}
          >
            {t('crm.closeLostConfirm')}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
